import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';

const SelfPacedPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleAccess = () => user ? navigate('/subscriptions') : navigate('/register');

  const faqs = [
    { q: 'What is self-paced learning?', a: 'Self-paced learning allows you to study anytime using recorded classes without fixed schedules — learn at your own speed.' },
    { q: 'Do I get lifetime access?', a: 'Access depends on your subscription plan. Check the subscriptions page for plan details and validity.' },
    { q: 'Are mock tests included?', a: 'Yes, full mock test series is included with the self-paced course.' },
    { q: 'Can I watch videos multiple times?', a: 'Yes, you can revise lectures anytime during your access period.' },
    { q: 'Is this suitable for beginners?', a: 'Yes, courses are designed from basic to advanced level to suit all learners.' },
  ];

  const courses = [
    { icon: '🎓', title: 'CUET UG Self-Paced Course', desc: 'Complete preparation for CUET Undergraduate with recorded lectures, subject-wise modules, and full mock tests.', badge: 'Most Popular' },
    { icon: '🎓', title: 'CUET PG Self-Paced Course', desc: 'Subject-specific CUET PG preparation with advanced topic coverage and practice sets.', badge: 'High Demand' },
  ];

  const features = [
    { icon: '📹', title: 'Pre-Recorded Video Lectures', desc: 'Learn anytime at your convenience' },
    { icon: '📝', title: 'Structured Course Modules', desc: 'Step-by-step topic coverage' },
    { icon: '🧪', title: 'Mock Test Series', desc: 'Practice with exam-level questions' },
    { icon: '📊', title: 'Performance Tracking', desc: 'Monitor your progress anytime' },
    { icon: '📂', title: 'Study Materials (PDFs)', desc: 'Download and revise anytime' },
    { icon: '🔄', title: 'Unlimited Revisions', desc: 'Watch lectures multiple times' },
  ];

  return (
    <>
      <Helmet>
        <title>Self-Paced Learning for CUET UG & PG | Recorded Classes & Mock Tests</title>
        <meta name="description" content="Join self-paced CUET UG & PG courses with recorded classes, mock tests & study materials. Learn anytime, anywhere and crack CUET with confidence." />
        <meta name="keywords" content="CUET self-paced learning, CUET recorded classes, CUET online course, CUET UG course, CUET PG course" />
        <link rel="canonical" href="https://besst.in/self-paced" />
        <meta property="og:title" content="Self-Paced Learning for CUET UG & PG | Recorded Classes & Mock Tests" />
        <meta property="og:description" content="Join self-paced CUET UG & PG courses with recorded classes, mock tests & study materials. Learn anytime, anywhere." />
        <meta property="og:url" content="https://besst.in/self-paced" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>
      <div className="min-h-screen pt-32 pb-20">
        {/* Hero */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">🎥 Study Anytime, Anywhere</div>
            <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-6 leading-tight">Self-Paced Learning for CUET UG &amp; PG</h1>
            <p className="text-xl text-text-muted mb-4">Expert-led recorded classes, structured learning paths, and full mock test series — at your own pace</p>
            <p className="text-text-muted mb-8">🎯 Flexible Learning &nbsp;•&nbsp; 👨‍🏫 Expert Faculty &nbsp;•&nbsp; 📚 Complete Preparation</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleAccess} className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">🚀 Explore Courses</button>
              <Link to="/subscriptions" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">View Plans &amp; Pricing</Link>
            </div>
            <p className="text-xs text-accent-gold mt-4">⚡ Limited access available</p>
          </div>
        </div>

        {/* Courses */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Choose Your Course &amp; Start Learning</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {courses.map(c => (
              <div key={c.title} className="glass-card relative">
                <span className="absolute -top-3 right-4 px-3 py-1 bg-primary/30 text-primary text-xs font-bold rounded-full">{c.badge}</span>
                <div className="text-4xl mb-4">{c.icon}</div>
                <h3 className="text-xl font-bold text-text-light mb-3">{c.title}</h3>
                <p className="text-text-muted text-sm mb-6">{c.desc}</p>
                <button onClick={handleAccess} className="btn-primary w-full py-3 font-semibold rounded-xl">▶ Access Course</button>
              </div>
            ))}
          </div>
        </div>

        {/* What you get */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Complete Learning Solution in One Place</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {features.map(f => (
              <div key={f.title} className="glass-card">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="text-text-light font-bold mb-1 text-sm">{f.title}</h3>
                <p className="text-text-muted text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why choose self-paced */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-text-light mb-6">🎯 Why Choose Self-Paced Learning?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Learn at your own speed — no pressure', 'Revise concepts multiple times', 'Balance studies with college or work', 'Access content anytime, anywhere', 'Save time with structured preparation', 'Track your performance regularly'].map(b => (
                <div key={b} className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl text-sm">
                  <span className="text-green-400">✔</span><span className="text-text-light">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Course Structure */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">🧠 How the Program is Structured</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { step: '01', label: 'Subject-wise modules' },
              { step: '02', label: 'Topic-wise video lectures' },
              { step: '03', label: 'Practice questions after each module' },
              { step: '04', label: 'Full-length mock tests' },
            ].map(s => (
              <div key={s.step} className="glass-card text-center p-4">
                <div className="text-2xl font-bold text-primary mb-2">{s.step}</div>
                <p className="text-text-muted text-sm">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it helps */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-text-light mb-4">📈 How It Helps You Crack CUET</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Build strong conceptual foundation', 'Practice regularly with mock tests', 'Improve accuracy and speed', 'Track your performance', 'Stay consistent in preparation', 'Learn from expert faculty'].map(b => (
                <div key={b} className="flex items-center gap-2 text-text-muted text-sm">
                  <span className="text-primary">→</span>{b}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Who is it for */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto text-center">
            <h2 className="text-xl font-bold text-text-light mb-6">🎯 Ideal for Students Who Want Flexibility</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {['CUET UG Aspirants', 'CUET PG Aspirants', 'College Students', 'Working Candidates', 'Repeat Aspirants'].map(t => (
                <span key={t} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Mock Test cross-sell */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-text-light mb-3">🧪 Mock Test Integration</h2>
            <p className="text-text-muted text-sm mb-4">Along with video lectures, you get full mock test series, topic-wise practice tests, and performance analysis.</p>
            <div className="flex flex-wrap gap-3">
              {['Full mock test series', 'Topic-wise practice tests', 'Performance analysis'].map(f => (
                <span key={f} className="px-3 py-1 bg-accent-gold/20 text-accent-gold rounded-full text-sm font-semibold">{f}</span>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">{f.q} <span className="text-primary text-xl">+</span></summary>
                <p className="text-text-muted mt-3 text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-3">Start Your CUET Preparation Today</h2>
            <p className="text-text-muted mb-2">Flexible, structured, and result-oriented preparation — at your own pace.</p>
            <p className="text-accent-gold text-sm mb-6">⚡ Limited access available</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleAccess} className="btn-primary px-8 py-3 font-bold rounded-xl">🚀 Enroll Now</button>
              <Link to="/subscriptions" className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">View Pricing</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelfPacedPage;
